import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  Zap, 
  Shield, 
  Target,
  ChevronRight,
  Activity,
  Settings,
  AlertTriangle,
  Brain,
  ArrowRight,
  Code,
  LineChart,
  BarChart3,
  User,
  Clock,
  CheckCircle
} from 'lucide-react';
import { ParticlesBackground } from '../components/ParticlesBackground';
import { CallBand } from '../components/CallBand';
import { BookCallModal } from '../components/BookCallModal';

const fadeUp = {
  initial: { opacity: 0, y: 15 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 }
};

const stagger = {
  animate: { transition: { staggerChildren: 0.06 } }
};

export default function HomePage() {
  const [showBookCallModal, setShowBookCallModal] = useState(false);
  
  const differentiators = [
    { icon: Brain, title: 'Rule-Based Logic', description: 'No black-box AI. Transparent, backtested rules you can verify and trust.' },
    { icon: Shield, title: 'Risk-First Philosophy', description: 'Every system we build starts with risk management, not profit targets.' },
    { icon: Settings, title: 'Trader-Built Systems', description: 'Developed by traders with real market experience, not just developers.' },
    { icon: Zap, title: 'Realistic Testing', description: 'All strategies tested with real spreads, slippage, and market conditions.' },
  ];

  const products = [
    // Monk Algo ORB - LIVE PRODUCT
    { 
      name: 'Monk Algo ORB', 
      description: 'Opening Range Breakout strategy for NAS100/MNQ. 2 variations with up to +73.58% backtested return and max drawdown as low as 2.51%. Available on MT5 + NinjaTrader 8.',
      status: 'Available Now',
      statusColor: 'text-[#10B981]',
      bgColor: 'bg-[#10B981]/10',
      borderColor: 'border-[#10B981]/20',
      icon: Clock,
      iconBg: 'bg-[#7c3aed]/15',
      iconColor: 'text-[#7c3aed]',
      link: '/orb-algo',
      isAvailable: true,
      stats: { return: 'Up to +73.58%', winRate: '76.85%', profitFactor: '2.93' }
    },
    // Monk Breakout EA - HIDDEN (not deleted, can be re-enabled later)
    // { 
    //   name: 'Monk Breakout EA', 
    //   description: 'Fully automated breakout trading system for MT5 with verified 2025 performance.',
    //   status: 'Available Now',
    //   statusColor: 'text-[#10B981]',
    //   bgColor: 'bg-[#10B981]/10',
    //   borderColor: 'border-[#10B981]/20',
    //   icon: TrendingUp,
    //   iconBg: 'bg-[#007AFF]/15',
    //   iconColor: 'text-[#007AFF]',
    //   link: '/ea',
    //   isAvailable: true,
    //   stats: { return: '+228.66%', winRate: '78.08%', profitFactor: '2.47' }
    // },
    // Inside Bar Algo - HIDDEN (not deleted, can be re-enabled later)
    // { 
    //   name: 'Inside Bar TradingView Algo', 
    //   description: 'Inside Bar Session Breakout strategy for XAUUSD on 5-minute timeframe with verified 117%+ annual return.',
    //   status: 'Available Now',
    //   statusColor: 'text-[#10B981]',
    //   bgColor: 'bg-[#10B981]/10',
    //   borderColor: 'border-[#10B981]/20',
    //   icon: BarChart3,
    //   iconBg: 'bg-[#8B5CF6]/15',
    //   iconColor: 'text-[#8B5CF6]',
    //   link: '/inside-bar',
    //   isAvailable: true,
    //   stats: { return: '+117.17%', winRate: '62.94%', profitFactor: '2.404' }
    // },
    { 
      name: 'Strategy Lab', 
      description: 'Free Pine Scripts & Indicators for manual traders. Test our trading concepts on TradingView.',
      status: 'Available Now',
      statusColor: 'text-[#10B981]',
      bgColor: 'bg-[#10B981]/10',
      borderColor: 'border-[#10B981]/20',
      icon: LineChart,
      iconBg: 'bg-[#8B5CF6]/15',
      iconColor: 'text-[#8B5CF6]',
      link: '/strategy-lab',
      isAvailable: true
    },
    { 
      name: 'Algo Strategies', 
      description: 'Additional automated strategies for different market conditions and trading styles.',
      status: 'In Development',
      statusColor: 'text-[#007AFF]',
      bgColor: 'bg-[#007AFF]/10',
      borderColor: 'border-[#007AFF]/20',
      icon: TrendingUp,
      iconBg: 'bg-[#10B981]/15',
      iconColor: 'text-[#10B981]',
      link: null,
      isAvailable: false
    },
  ];

  return (
    <div className="min-h-screen bg-[#050508]">
      {/* Call Band - Below Header */}
      <div className="pt-20">
        <CallBand onBookCall={() => setShowBookCallModal(true)} />
      </div>
      
      {/* Hero Section - Company Focused */}
      <section className="relative min-h-[auto] md:min-h-[85vh] flex items-center overflow-hidden premium-bg pt-8 pb-6 md:py-0" data-testid="hero-section">
        <div className="gradient-orb gradient-orb-1" />
        <div className="gradient-orb gradient-orb-2" />
        <div className="gradient-orb gradient-orb-3" />
        <div className="mesh-overlay" />
        <div className="candle-pattern" />
        <div className="absolute inset-0 z-[1]"><ParticlesBackground /></div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-0 md:pt-24 pb-0 md:pb-8">
          <motion.div initial="initial" animate="animate" variants={stagger} className="max-w-3xl">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-3 md:mb-4">
              <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
              <span className="font-mono text-sm text-white/70">Algorithmic Trading Systems</span>
            </motion.div>

            <motion.h1 variants={fadeUp} className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl text-white hero-title mb-3 md:mb-4" data-testid="hero-title">
              We Build Trading Systems{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#007AFF] via-[#8B5CF6] to-[#10B981]">That Work</span>
            </motion.h1>

            <motion.p variants={fadeUp} className="font-body text-sm md:text-lg text-white/55 max-w-xl mb-5 md:mb-6 leading-relaxed" data-testid="hero-subtitle">
              Monk Algo is a team of trading strategists focused on developing, testing, and continuously improving 
              automated trading systems for MT5 and NinjaTrader 8. We don't launch until it's proven.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-3">
              <Link to="/orb-algo" className="btn-purple flex items-center gap-2 group" data-testid="hero-cta-products">
                <Target size={16} />
                Explore Our Products
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href="#about" className="btn-secondary flex items-center gap-2" data-testid="hero-cta-about">
                <User size={16} />
                About Us
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="section-compact section-depth" id="what-we-do" data-testid="what-we-do-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto text-center">
            <span className="font-mono text-[#8B5CF6] text-xs uppercase tracking-wider mb-2 block">What We Do</span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">Continuous Strategy Development</h2>
            <p className="font-body text-white/55 text-sm md:text-base leading-relaxed">
              Our team spends every day researching market conditions, running multi-year backtests, and refining strategies. 
              When a system proves itself in testing, we release it to subscribers — and we keep updating it as markets evolve. 
              This is not a set-and-forget tool. It's an active, maintained trading system backed by ongoing strategy work.
            </p>
          </motion.div>

          {/* Process Steps */}
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="glass rounded-xl p-4 text-center">
              <div className="w-10 h-10 rounded-lg bg-[#007AFF]/15 flex items-center justify-center mx-auto mb-3">
                <Brain className="w-5 h-5 text-[#007AFF]" />
              </div>
              <h3 className="font-heading font-semibold text-white text-sm mb-1">Research</h3>
              <p className="font-body text-xs text-white/40">Market analysis & strategy ideation</p>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/15 flex items-center justify-center mx-auto mb-3">
                <Code className="w-5 h-5 text-[#8B5CF6]" />
              </div>
              <h3 className="font-heading font-semibold text-white text-sm mb-1">Develop</h3>
              <p className="font-body text-xs text-white/40">Build & code trading systems</p>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="w-10 h-10 rounded-lg bg-[#10B981]/15 flex items-center justify-center mx-auto mb-3">
                <Activity className="w-5 h-5 text-[#10B981]" />
              </div>
              <h3 className="font-heading font-semibold text-white text-sm mb-1">Backtest</h3>
              <p className="font-body text-xs text-white/40">Multi-year historical testing</p>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/15 flex items-center justify-center mx-auto mb-3">
                <Clock className="w-5 h-5 text-[#F59E0B]" />
              </div>
              <h3 className="font-heading font-semibold text-white text-sm mb-1">Maintain</h3>
              <p className="font-body text-xs text-white/40">Continuous updates & support</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Products Section */}
      <section className="section-compact" id="products" data-testid="products-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-8">
            <span className="font-mono text-[#10B981] text-xs uppercase tracking-wider mb-2 block">Our Products</span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-2">Trading Systems & Tools</h2>
            <p className="font-body text-white/45 text-sm">Professional-grade tools for serious traders</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4">
            {products.map((product, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 15 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ duration: 0.3, delay: index * 0.08 }}
                className="relative"
              >
                {product.isAvailable ? (
                  <Link to={product.link} className="group block glass rounded-2xl p-6 h-full card-hover border border-transparent hover:border-[#8B5CF6]/30 transition-all" data-testid={`product-card-${index}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-12 h-12 rounded-xl ${product.iconBg} flex items-center justify-center`}>
                        <product.icon className={`w-6 h-6 ${product.iconColor}`} />
                      </div>
                      <span className={`px-2.5 py-1 rounded-full ${product.bgColor} border ${product.borderColor} text-[10px] font-mono ${product.statusColor}`}>
                        {product.status}
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-lg text-white mb-2 group-hover:text-[#8B5CF6] transition-colors">{product.name}</h3>
                    <p className="font-body text-sm text-white/50 mb-4 leading-relaxed">{product.description}</p>
                    
                    {product.stats && (
                      <div className="grid grid-cols-3 gap-2 mb-4 p-3 rounded-lg bg-white/3">
                        <div className="text-center">
                          <div className="font-mono font-bold text-sm text-[#10B981]">{product.stats.return}</div>
                          <div className="font-body text-[9px] text-white/40">Return</div>
                        </div>
                        <div className="text-center">
                          <div className="font-mono font-bold text-sm text-[#007AFF]">{product.stats.winRate}</div>
                          <div className="font-body text-[9px] text-white/40">Win Rate</div>
                        </div>
                        <div className="text-center">
                          <div className="font-mono font-bold text-sm text-[#8B5CF6]">{product.stats.profitFactor}</div>
                          <div className="font-body text-[9px] text-white/40">Profit Factor</div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-[#8B5CF6] text-sm font-medium">
                      <span>View Details</span>
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                ) : (
                  <div className="glass rounded-2xl p-6 h-full opacity-80" data-testid={`product-card-${index}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-12 h-12 rounded-xl ${product.iconBg} flex items-center justify-center`}>
                        <product.icon className={`w-6 h-6 ${product.iconColor}`} />
                      </div>
                      <span className={`px-2.5 py-1 rounded-full ${product.bgColor} border ${product.borderColor} text-[10px] font-mono ${product.statusColor}`}>
                        {product.status}
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-lg text-white mb-2">{product.name}</h3>
                    <p className="font-body text-sm text-white/50 mb-4 leading-relaxed">{product.description}</p>
                    <div className="flex items-center gap-2 text-white/30 text-sm">
                      <Clock size={14} />
                      <span>Coming soon</span>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Monk Algo */}
      <section className="section-compact section-glow" id="difference" data-testid="difference-section">
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-6">
            <span className="font-mono text-[#10B981] text-xs uppercase tracking-wider mb-2 block">Our Philosophy</span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-2">Why Monk Algo</h2>
            <p className="font-body text-white/45 text-sm max-w-lg mx-auto">What sets our approach apart from typical trading tools.</p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {differentiators.map((item, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.3, delay: index * 0.06 }} className="glass rounded-xl p-5 card-hover" data-testid={`differentiator-card-${index}`}>
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#8B5CF6]/15 to-[#10B981]/15 flex items-center justify-center mb-3">
                  <item.icon className="w-4 h-4 text-[#8B5CF6]" />
                </div>
                <h3 className="font-heading font-semibold text-white mb-1 text-sm">{item.title}</h3>
                <p className="font-body text-xs text-white/45 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About / Founder Section */}
      <section className="section-compact" id="about" data-testid="about-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="font-mono text-[#007AFF] text-xs uppercase tracking-wider mb-2 block">About Us</span>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">Built by Traders, for Traders</h2>
              <p className="font-body text-white/55 text-sm leading-relaxed mb-4">
                Monk Algo was founded by <span className="text-white font-medium">Sahil Aswani</span>, a trader with 
                <span className="text-[#10B981] font-medium"> 8 years of experience</span> in the markets. After years of 
                struggling with the limitations of manual trading — the emotional decisions, the missed opportunities, 
                the inconsistent execution — we set out to build something better.
              </p>
              <p className="font-body text-white/55 text-sm leading-relaxed mb-6">
                Our mission is simple: develop trading systems that combine rigorous strategy research with fully 
                automated execution. Systems that are transparent, tested, and continuously improved. No black boxes. 
                No unrealistic promises. Just solid, rule-based trading.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#10B981]" />
                  <span className="font-body text-sm text-white/60">8+ Years Trading Experience</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#10B981]" />
                  <span className="font-body text-sm text-white/60">Transparent Backtested Results</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#10B981]" />
                  <span className="font-body text-sm text-white/60">Active Strategy Development</span>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#007AFF]/20 via-[#8B5CF6]/10 to-[#10B981]/20 rounded-3xl blur-2xl opacity-50" />
              <div className="relative glass-strong rounded-2xl p-8 border border-white/10">
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src="/founder.jpg" 
                    alt="Sahil Aswani" 
                    className="w-16 h-16 rounded-xl object-cover"
                  />
                  <div>
                    <h3 className="font-heading font-bold text-xl text-white">Sahil Aswani</h3>
                    <p className="font-body text-sm text-white/50">Founder & Lead Strategist</p>
                  </div>
                </div>
                <blockquote className="font-body text-white/60 text-sm leading-relaxed italic border-l-2 border-[#8B5CF6]/50 pl-4">
                  "I've spent years testing strategies, losing money on bad systems, and learning what actually works 
                  in real markets. Monk Algo is the result of that journey — systems I trust enough to trade with 
                  my own capital."
                </blockquote>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA - No Price */}
      <section className="section-compact" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="relative glass-strong rounded-2xl p-8 md:p-10 text-center overflow-hidden">
            <div className="absolute inset-0" style={{background: 'radial-gradient(ellipse at center, rgba(139, 92, 246, 0.1) 0%, transparent 70%)'}} />
            <div className="relative z-10">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-white mb-3">Ready to Automate Your Trading?</h2>
              <p className="font-body text-white/50 max-w-md mx-auto mb-5 text-sm">
                Explore our current products or learn more about our approach to algorithmic trading.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to="/orb-algo" className="btn-purple flex items-center gap-2" data-testid="cta-btn-products">
                  <Target size={16} /> View Our Products
                </Link>
                <a href="#about" className="btn-secondary flex items-center gap-2" data-testid="cta-btn-about">
                  <User size={16} /> Learn About Us
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-4" data-testid="disclaimer-section">
        <div className="max-w-3xl mx-auto px-6">
          <div className="risk-disclaimer flex items-start gap-2.5">
            <AlertTriangle className="w-4 h-4 text-yellow-500/60 flex-shrink-0 mt-0.5" />
            <p className="font-body text-[11px] text-white/35 leading-relaxed">
              <strong className="text-white/45">Risk Disclaimer:</strong> Trading forex, CFDs, and other financial instruments involves significant risk. Past performance is not indicative of future results. Only trade with capital you can afford to lose.
            </p>
          </div>
        </div>
      </section>
      
      {/* Book Call Modal */}
      <BookCallModal 
        isOpen={showBookCallModal} 
        onClose={() => setShowBookCallModal(false)} 
      />
    </div>
  );
}
